import {AuthModel} from './_models'

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'

/**
 * Validates if the parsed data matches the AuthModel structure
 */
const isValidAuthModel = (data: any): data is AuthModel => {
  return (
    data !== null &&
    typeof data === 'object' &&
    typeof data.api_token === 'string' &&
    data.api_token.length > 0
  )
}

/**
 * Safely checks if localStorage is available
 */
const isLocalStorageAvailable = (): boolean => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return false
    }
    // Test localStorage access
    const test = '__localStorage_test__'
    localStorage.setItem(test, test)
    localStorage.removeItem(test)
    return true
  } catch {
    return false
  }
}

/**
 * Gets authentication data from localStorage with proper error handling and validation
 * Automatically cleans up invalid/corrupted data
 */
const getAuth = (): AuthModel | undefined => {
  if (!isLocalStorageAvailable()) {
    return undefined
  }

  let lsValue: string | null = null
  try {
    lsValue = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.warn('AUTH LOCAL STORAGE GET ERROR: Failed to read from localStorage', error)
    return undefined
  }

  if (!lsValue || lsValue.trim() === '') {
    return undefined
  }

  // Check if the value looks like JSON (starts with { or [)
  const trimmedValue = lsValue.trim()
  if (!trimmedValue.startsWith('{') && !trimmedValue.startsWith('[')) {
    console.warn(
      'AUTH LOCAL STORAGE PARSE ERROR: Invalid data format detected. Cleaning up corrupted data.',
      {valuePreview: trimmedValue.substring(0, 50)}
    )
    // Clean up invalid data
    try {
      localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
    } catch (cleanupError) {
      console.error('AUTH LOCAL STORAGE CLEANUP ERROR', cleanupError)
    }
    return undefined
  }

  try {
    const parsed = JSON.parse(lsValue)
    
    // Validate the parsed data structure
    if (!isValidAuthModel(parsed)) {
      console.warn(
        'AUTH LOCAL STORAGE VALIDATION ERROR: Data does not match AuthModel structure. Cleaning up.',
        {parsed}
      )
      // Clean up invalid data
      try {
        localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
      } catch (cleanupError) {
        console.error('AUTH LOCAL STORAGE CLEANUP ERROR', cleanupError)
      }
      return undefined
    }

    return parsed
  } catch (error) {
    console.warn(
      'AUTH LOCAL STORAGE PARSE ERROR: Failed to parse JSON. Cleaning up corrupted data.',
      error
    )
    // Clean up corrupted data
    try {
      localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
    } catch (cleanupError) {
      console.error('AUTH LOCAL STORAGE CLEANUP ERROR', cleanupError)
    }
    return undefined
  }
}

/**
 * Saves authentication data to localStorage with proper error handling
 */
const setAuth = (auth: AuthModel) => {
  if (!isLocalStorageAvailable()) {
    console.warn('AUTH LOCAL STORAGE SAVE ERROR: localStorage is not available')
    return
  }

  // Validate auth data before saving
  if (!isValidAuthModel(auth)) {
    console.error('AUTH LOCAL STORAGE SAVE ERROR: Invalid auth data structure', auth)
    return
  }

  try {
    const lsValue = JSON.stringify(auth)
    localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue)
    // Dispatch custom event to notify components about auth change
    window.dispatchEvent(new Event('auth-storage-changed'))
  } catch (error) {
    // Handle quota exceeded or other storage errors
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.error('AUTH LOCAL STORAGE SAVE ERROR: Storage quota exceeded', error)
    } else {
      console.error('AUTH LOCAL STORAGE SAVE ERROR: Failed to save to localStorage', error)
    }
  }
}

/**
 * Removes authentication data from localStorage
 */
const removeAuth = () => {
  if (!isLocalStorageAvailable()) {
    return
  }

  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
    // Dispatch custom event to notify components about auth change
    window.dispatchEvent(new Event('auth-storage-changed'))
  } catch (error) {
    console.error('AUTH LOCAL STORAGE REMOVE ERROR', error)
  }
}

export function setupAxios(axios: any) {
  axios.defaults.headers.Accept = 'application/json'
  axios.interceptors.request.use(
    (config: {headers: {Authorization: string}}) => {
      const auth = getAuth()
      if (auth && auth.api_token) {
        config.headers.Authorization = `Bearer ${auth.api_token}`
      }

      return config
    },
    (err: any) => Promise.reject(err)
  )
}

export {getAuth, setAuth, removeAuth, AUTH_LOCAL_STORAGE_KEY}
